import { useCallback, useEffect, useMemo, useState } from 'react';

import ChartDetailsCard from '../../../components/ChartDetailsCard';
import ColumnChart from '../../../components/Charts/ColumnChart';
import LineColumnChart from '../../../components/Charts/LineColumnChart';
import PieChart from '../../../components/Charts/PieChart';
import DataSelector from '../../../components/DataSelector';
import ScrollWrapper from '../../../components/ScrollWrapper';
import { withLinks } from '../../../link-mapper';
import { descriptions, marketData } from '../../../presentation/data';
import { YearbookMarket, YearbookMarketChartInfo } from '../../../types';
import { getAdvertisementByPosition } from '../../../utils';
import * as Stock from '../styles';

interface StockSectionProps {
  market: YearbookMarket;
}

export function StockSection({ market }: StockSectionProps) {
  const [selectedChartInfo, setSelectedChartInfo] =
    useState<YearbookMarketChartInfo | null>(null);

  const items = useMemo(
    () => marketData[market.name].filter((info) => info.section === 'stock'),
    [market],
  );

  const ads = useMemo(
    () => getAdvertisementByPosition(market.name, 2),
    [market],
  );

  const getAdvertisementImage = (imageName: string) => {
    return require(`src/assets/imgs/yearbook/2024/banners/${imageName}.png`);
  };

  const ChartComponent = useMemo(() => {
    if (selectedChartInfo?.chart?.chartType === 'line-column')
      return LineColumnChart;
    else if (selectedChartInfo?.chart?.chartType === 'column')
      return ColumnChart;
    else return PieChart;
  }, [selectedChartInfo]);

  const description = useMemo(
    () => descriptions[market.name].stock,
    [market.name],
  );

  const getChartInfoByName = useCallback(
    (name: string) => {
      return marketData[market.name].find(
        (info) => info.section === 'stock' && info.name === name,
      );
    },
    [market],
  );

  const selectChartInfo = useCallback(
    (selectedName: string) => {
      const chartInfo = getChartInfoByName(selectedName) ?? null;
      setSelectedChartInfo(chartInfo);
    },
    [market],
  );

  useEffect(() => {
    setSelectedChartInfo(items[0]);
  }, [market]);

  return (
    <Stock.Section data-yearbook-section="Estoque">
      <Stock.Title>Estoque</Stock.Title>
      <Stock.Description>
        {description.map((text, idx) => (
          <Stock.DescriptionParagraph
            key={`${market}-${idx}`}
            dangerouslySetInnerHTML={{ __html: withLinks(text) }}
          />
        ))}
      </Stock.Description>
      <ScrollWrapper
        items={items}
        select={selectChartInfo}
        selected={selectedChartInfo}>
        {selectedChartInfo && (
          <Stock.Content>
            <ChartDetailsCard details={selectedChartInfo.details} />
            <Stock.ChartContent>
              <DataSelector
                select={selectChartInfo}
                selected={selectedChartInfo}
                items={items}
                id="stock-selector"
              />
              <Stock.ChartTip>
                Passe o mouse sobre o gráfico para visualizar a legenda dos
                dados.
              </Stock.ChartTip>
              <Stock.ChartWrapper>
                {selectedChartInfo?.chart?.chartType && (
                  <ChartComponent
                    data={selectedChartInfo.chart.data as any}
                    categories={selectedChartInfo.chart.categories}
                    numeric={selectedChartInfo.chart.numeric}
                    stacked={selectedChartInfo.chart.stacked}
                    monetary={selectedChartInfo.chart.monetary}
                    animationEnabled={selectedChartInfo.chart.animated ?? true}
                  />
                )}
                {selectedChartInfo.link && (
                  <Stock.Button href={selectedChartInfo.link}>
                    Acessar plataforma Uqbar
                  </Stock.Button>
                )}
              </Stock.ChartWrapper>
            </Stock.ChartContent>
          </Stock.Content>
        )}
      </ScrollWrapper>
      {ads && (
        <Stock.Ads
          image={getAdvertisementImage(ads.banner).default}
          href={ads.url}
          target="_blank"
        />
      )}
    </Stock.Section>
  );
}
